import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Database, Plus, ArrowRight, Settings, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CmsCollectionService, type CmsCollection } from "@/services/cms";

const CmsCollections: React.FC = () => {
  const [, setLocation] = useLocation();
  const { isSuperAdmin } = useAuth();
  const [collections, setCollections] = useState<CmsCollection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setIsLoading(true);
    setError("");
    try {
      const data = await CmsCollectionService.list();
      setCollections(data);
    } catch (err: any) {
      console.error("Failed to load collections:", err);
      setError("Failed to load collections. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (collection: CmsCollection) => {
    if (
      !window.confirm(
        `Delete the "${collection.name}" collection? This only works if it has no items left.`
      )
    ) {
      return;
    }
    try {
      await CmsCollectionService.remove(collection.id);
      load();
    } catch (err: any) {
      window.alert(err?.message || "Failed to delete collection.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-gray-600">Loading content collections...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <Database className="h-8 w-8 text-primary shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Content (CMS)</h1>
            <p className="text-gray-600">
              Collections of content shown on the public site — blog posts, and anything else a page needs.
            </p>
          </div>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setLocation("/cms/collections/new")}
            className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>New Collection</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {collections.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg px-6 py-12 text-center text-gray-500">
          <Database className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium">No collections yet</p>
          <p className="text-sm">
            {isSuperAdmin
              ? "Create one to start adding content for a page on the public site."
              : "Ask a super admin to set one up."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map((collection) => (
            <div
              key={collection.id}
              className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{collection.name}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">/{collection.key}</p>
                </div>
                {isSuperAdmin && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setLocation(`/cms/collections/${collection.id}/edit`)}
                      className="text-gray-400 hover:text-primary transition-colors"
                      title="Edit schema"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(collection)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete collection"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {collection.description && (
                <p className="text-sm text-gray-600 flex-1">{collection.description}</p>
              )}

              <p className="text-xs text-gray-500">
                {collection.fields.length} field{collection.fields.length === 1 ? "" : "s"}
                {collection.hasSlug ? " · has URL slug" : ""}
              </p>

              <button
                onClick={() => setLocation(`/cms/${collection.key}`)}
                className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/5 transition-colors mt-1"
              >
                <span>Open items</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CmsCollections;
